import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogClose, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import './AirplaneDetailModal.css';

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

const formatValue = (value) => {
  if (!hasValue(value)) return 'N/A';
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? value.toLocaleString()
      : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return value;
};

const SpecField = ({ label, value, unit, isKeySpec = false }) => (
  <div className={cn('detail-spec-row', isKeySpec && 'detail-spec-row--key')}>
    <span className="detail-spec-label">{label}</span>
    <span className="detail-spec-value">
      {formatValue(value)}
      {unit && hasValue(value) ? <span className="detail-spec-unit">{unit}</span> : null}
    </span>
  </div>
);

const MetricCard = ({ label, value, unit }) => {
  if (!hasValue(value)) return null;

  return (
    <article className="detail-metric-card">
      <p className="detail-metric-label">{label}</p>
      <p className="detail-metric-value">
        {formatValue(value)}
        {unit ? <span className="detail-metric-unit">{unit}</span> : null}
      </p>
    </article>
  );
};

const EditorialSection = ({ kicker, title, children, className }) => (
  <section className={cn('detail-panel', className)}>
    {kicker ? <p className="detail-section-kicker">{kicker}</p> : null}
    {title ? <h3 className="detail-section-title">{title}</h3> : null}
    <div className="detail-section-body">{children}</div>
  </section>
);

const AirplaneDetailModal = ({
  detailData,
  onClose,
  onNavigate: _onNavigate,
  canNavigate: _canNavigate,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef(null);
  const imageContainerRef = useRef(null);
  const isScrolledRef = useRef(false);
  const closeButtonRef = useRef(null);
  const lastPlaneRef = useRef(null);
  const motionRef = useRef({
    current: { y: 0, scale: 1, opacity: 1 },
    target: { y: 0, scale: 1, opacity: 1 },
    rafId: 0,
    lastTime: 0,
  });
  const prefersReducedMotionRef = useRef(false);

  useEffect(() => {
    if (detailData?.plane) {
      lastPlaneRef.current = detailData.plane;
    }
  }, [detailData]);

  const plane = detailData?.plane ?? lastPlaneRef.current;
  const open = Boolean(detailData);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => {
      prefersReducedMotionRef.current = media.matches;
    };
    updatePreference();
    if (media.addEventListener) {
      media.addEventListener('change', updatePreference);
      return () => media.removeEventListener('change', updatePreference);
    }
    media.addListener(updatePreference);
    return () => media.removeListener(updatePreference);
  }, []);

  const applyImageStyles = useCallback((y, scale, opacity) => {
    if (!imageContainerRef.current) return;
    imageContainerRef.current.style.transform = `translate3d(0px, ${y}px, 0px) scale(${scale})`;
    imageContainerRef.current.style.opacity = `${opacity}`;
  }, []);

  const animateImage = useCallback(() => {
    const motion = motionRef.current;
    motion.rafId = 0;

    if (prefersReducedMotionRef.current) {
      const { y, scale, opacity } = motion.target;
      motion.current = { y, scale, opacity };
      applyImageStyles(y, scale, opacity);
      return;
    }

    const now = window.performance.now();
    if (!motion.lastTime) {
      motion.lastTime = now;
    }
    const delta = Math.min(48, now - motion.lastTime);
    motion.lastTime = now;
    const ease = 1 - Math.exp(-delta / 100);

    motion.current.y += (motion.target.y - motion.current.y) * ease;
    motion.current.scale += (motion.target.scale - motion.current.scale) * ease;
    motion.current.opacity += (motion.target.opacity - motion.current.opacity) * ease;

    applyImageStyles(motion.current.y, motion.current.scale, motion.current.opacity);

    const isSettled =
      Math.abs(motion.target.y - motion.current.y) < 0.1 &&
      Math.abs(motion.target.scale - motion.current.scale) < 0.001 &&
      Math.abs(motion.target.opacity - motion.current.opacity) < 0.01;

    if (!isSettled) {
      motion.rafId = window.requestAnimationFrame(animateImage);
    }
  }, [applyImageStyles]);

  const computeTargets = useCallback((scrollTop) => {
    const progress = Math.min(1, Math.max(0, scrollTop / 480));
    return {
      y: -progress * 140,
      scale: 1 - progress * 0.18,
      opacity: 1 - progress * 0.55,
    };
  }, []);

  const updateTargets = useCallback(
    (scrollTop) => {
      const motion = motionRef.current;
      motion.target = computeTargets(scrollTop);

      if (!motion.rafId) {
        motion.lastTime = window.performance.now();
        motion.rafId = window.requestAnimationFrame(animateImage);
      }
    },
    [animateImage, computeTargets]
  );

  const handleScroll = useCallback(
    (e) => {
      const scrollTop = e.target.scrollTop;
      const nextIsScrolled = scrollTop > 10;
      if (nextIsScrolled !== isScrolledRef.current) {
        isScrolledRef.current = nextIsScrolled;
        setIsScrolled(nextIsScrolled);
      }
      updateTargets(scrollTop);
    },
    [updateTargets]
  );

  useEffect(() => {
    if (detailData) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
      const motion = motionRef.current;
      if (motion.rafId) {
        window.cancelAnimationFrame(motion.rafId);
        motion.rafId = 0;
      }
      motion.lastTime = 0;
      const targets = {
        y: 0,
        scale: 1,
        opacity: 1,
      };
      if (prefersReducedMotionRef.current) {
        motion.current = targets;
        motion.target = targets;
        applyImageStyles(targets.y, targets.scale, targets.opacity);
      } else {
        const entry = { y: 24, scale: 1.05, opacity: 0 };
        motion.current = entry;
        motion.target = targets;
        applyImageStyles(entry.y, entry.scale, entry.opacity);
        motion.lastTime = window.performance.now();
        motion.rafId = window.requestAnimationFrame(animateImage);
      }
      isScrolledRef.current = false;
      setIsScrolled(false);
      closeButtonRef.current?.focus();
    }
  }, [detailData, applyImageStyles, animateImage]);

  useEffect(() => {
    const motion = motionRef.current;
    return () => {
      if (motion.rafId) {
        window.cancelAnimationFrame(motion.rafId);
        motion.rafId = 0;
      }
    };
  }, []);

  if (!plane && !open) {
    return null;
  }

  const performanceDetails = [
    { label: 'Max range', value: plane?.max_range, unit: 'km' },
    { label: 'Cruise speed', value: plane?.cruise_speed, unit: 'km/h' },
    { label: 'Max passengers', value: plane?.max_passengers, unit: 'pax' },
  ];

  const dimensionDetails = [
    { label: 'Length', value: plane?.length, unit: 'm' },
    { label: 'Wingspan', value: plane?.wingspan, unit: 'm' },
    { label: 'Height', value: plane?.height, unit: 'm' },
  ];

  const historyDetails = [
    { label: 'First flight', value: plane?.first_flight },
    { label: 'Entry into service', value: plane?.entry_into_service },
    { label: 'Status', value: plane?.status },
    { label: 'Production count', value: plane?.production_count },
  ];

  const propulsionDetails = [
    { label: 'Engine', value: plane?.engine || 'Pratt & Whitney PW4000 x4' },
    { label: 'Thrust', value: plane?.thrust || '282', unit: 'kN' },
  ];

  const cabinDetails = [
    { label: 'Cabin width', value: plane?.cabin_width || '5.6', unit: 'm' },
    { label: 'Cabin height', value: plane?.cabin_height || '2.4', unit: 'm' },
    { label: 'Cabin noise', value: plane?.cabin_noise_level || 'Quiet' },
    { label: 'Pressurization altitude', value: plane?.pressurization_altitude || '6,000 ft' },
    { label: 'Cargo capacity', value: plane?.cargo_capacity || '170', unit: 'm3' },
  ];

  const titleMeta = [
    plane?.manufacturer,
    plane?.type,
    plane?.status || (plane?.entry_into_service ? `In service since ${plane.entry_into_service}` : null),
  ].filter(hasValue);

  const identityDetails = [
    { label: 'Manufacturer', value: plane?.manufacturer },
    { label: 'Model', value: plane?.model },
    { label: 'Class', value: plane?.type },
    {
      label: plane?.entry_into_service ? 'Service entry' : 'Status',
      value: plane?.entry_into_service || plane?.status,
    },
  ].filter((item) => hasValue(item.value));

  const specGroups = [
    { title: 'Performance', items: performanceDetails },
    { title: 'Dimensions', items: dimensionDetails },
    { title: 'Program', items: historyDetails },
    { title: 'Propulsion', items: propulsionDetails },
    { title: 'Cabin', items: cabinDetails },
  ];

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent
        disableDefaultAnimation
        overlayClassName="detail-dialog-overlay"
        className="detail-dialog detail-dialog--airplane w-[min(95vw,1100px)] max-w-none overflow-hidden p-0 focus:outline-none focus-visible:outline-none"
      >
        <DialogTitle className="sr-only">{plane?.name || 'Airplane Detail'}</DialogTitle>
        <div className="flex h-full flex-col">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="detail-scroll flex-1 overflow-y-auto pb-10"
          >
            <div className="detail-hero-wrap">
              <div
                ref={imageContainerRef}
                className="detail-hero-media h-[320px] w-full overflow-hidden border-b border-border/60 shadow-lg sm:h-[460px] lg:h-[520px]"
              >
                <img
                  src={plane?.image_url}
                  alt={plane?.name}
                  className="detail-hero-image"
                />
              </div>
            </div>

            <div className="mx-auto w-full max-w-6xl px-6 pt-10 sm:px-8">
              <div className="detail-content-shell">
                <section className={cn('detail-intro-grid', isScrolled && 'detail-intro-grid--scrolled')}>
                  <div className="detail-intro-copy">
                    <p className="detail-kicker">Aircraft dossier</p>
                    <h2 className="detail-title">{plane?.name}</h2>
                    {titleMeta.length > 0 ? (
                      <div className="detail-subtitle-list" aria-label="Aircraft summary">
                        {titleMeta.map((item) => (
                          <span key={item}>{item}</span>
                        ))}
                      </div>
                    ) : null}
                    {plane?.description ? <p className="detail-lead">{plane.description}</p> : null}
                  </div>

                  {identityDetails.length > 0 ? (
                    <div className="detail-identity-card" aria-label="Aircraft identity">
                      {identityDetails.map((item) => (
                        <div className="detail-identity-item" key={item.label}>
                          <p className="detail-identity-label">{item.label}</p>
                          <p className="detail-identity-value">{formatValue(item.value)}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </section>

                <section className="detail-metric-grid" aria-label="Key metrics">
                  {performanceDetails.map((item) => (
                    <MetricCard {...item} key={item.label} />
                  ))}
                </section>

                <div className="detail-body-grid">
                  <div className="detail-main-stack">
                    {plane?.interesting_story || plane?.fun_fact ? (
                      <section className="detail-highlight-grid">
                        {plane?.interesting_story ? (
                          <article className="detail-highlight-card">
                            <p className="detail-section-kicker">Interesting story</p>
                            <h3 className="detail-highlight-title">A little narrative behind the airframe</h3>
                            <p className="detail-highlight-text">{plane.interesting_story}</p>
                          </article>
                        ) : null}

                        {plane?.fun_fact ? (
                          <article className="detail-highlight-card">
                            <p className="detail-section-kicker">Fun fact</p>
                            <h3 className="detail-highlight-title">One detail that tends to stick</h3>
                            <p className="detail-highlight-text">{plane.fun_fact}</p>
                          </article>
                        ) : null}
                      </section>
                    ) : null}

                    {plane?.wireframe_image_url ? (
                      <EditorialSection
                        kicker="Structural view"
                        title="Airframe proportions and silhouette"
                      >
                        <div className="detail-wireframe-frame">
                          <img
                            src={plane.wireframe_image_url}
                            alt={`${plane.name} Wireframe`}
                            className="detail-wireframe-image"
                          />
                        </div>
                      </EditorialSection>
                    ) : null}

                    <EditorialSection
                      kicker="Aviation knowledge"
                      title="Small details worth noticing"
                    >
                      <div className="detail-knowledge-grid">
                        <p className="detail-note">
                          The small hole in airplane windows helps equalize pressure between
                          the cabin and the outside, reducing stress on the window panes.
                        </p>
                        <p className="detail-note">
                          The Wright brothers completed the first powered flight in 1903.
                          Modern high-speed aircraft like the SR-71 Blackbird reached around
                          Mach 3.3.
                        </p>
                      </div>
                    </EditorialSection>
                  </div>

                  <aside className="detail-rail">
                    <section className="detail-panel detail-dossier">
                      <div className="detail-dossier-header">
                        <p className="detail-section-kicker">Technical dossier</p>
                        <h3 className="detail-section-title">Specifications</h3>
                        <p className="detail-dossier-text">
                          A distilled reference covering performance, proportions, program
                          history, propulsion, and cabin configuration.
                        </p>
                      </div>

                      {specGroups.map(({ title, items }) => {
                        const visibleItems = items.filter((item) => hasValue(item.value));

                        if (!visibleItems.length) {
                          return null;
                        }

                        return (
                          <div className="detail-dossier-group" key={title}>
                            <h4 className="detail-dossier-group-title">{title}</h4>
                            <div className="detail-dossier-grid">
                              {visibleItems.map((item, index) => (
                                <SpecField
                                  {...item}
                                  key={item.label}
                                  isKeySpec={title === 'Performance' && index === 0}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </section>
                  </aside>
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogClose asChild>
          <Button
            ref={closeButtonRef}
            type="button"
            variant="ghost"
            size="icon"
            className="detail-close-button absolute right-4 top-4 rounded-full shadow-md"
            aria-label="Close"
          >
            <span className="text-lg">x</span>
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
};

export default AirplaneDetailModal;
